import { QueryClient } from '@tanstack/react-query';
import { isErrorCode } from '@xdocs/i18n';

const MAX_RETRIES = 3;

/**
 * WS analog of "don't retry a 4xx" (typescript.md): the closed `ErrorCode`
 * set (E12 §16) stands in for HTTP status class. `VALIDATION`/
 * `UNAUTHENTICATED`/`FORBIDDEN`/`NOT_FOUND`/`CONFLICT`/`INSUFFICIENT_CREDITS`/
 * `TIER_REQUIRED`/`CONTENT_POLICY_VIOLATION`/`PROMO_INVALID`/`BAD_OP` are
 * caller-fixable, not transient — retrying just re-fails the same way.
 * `INTERNAL`/`RATE_LIMITED` (and anything not a recognized `ErrorCode`,
 * e.g. a raw network/timeout failure) are worth a bounded retry.
 */
function isRetryableWsError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: unknown }).code;
    if (typeof code === 'string' && isErrorCode(code)) {
      return code === 'INTERNAL' || code === 'RATE_LIMITED';
    }
  }
  return true;
}

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => failureCount < MAX_RETRIES && isRetryableWsError(error),
      },
      mutations: {
        // Never blind-auto-retry a mutation (non-idempotent unless the op
        // itself is designed for it, typescript.md) — callers opt in per op.
        retry: false,
      },
    },
  });
}
